import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import {
    REQUEST_BADGE_ATTRIBUTES,
    REQUEST_LATEST_EARNED_BADGES,
    FETCH_BADGES_LEADERBOARD,
    REQUEST_AVAILABLE_BADGES,
    REQUEST_COMPARISON_BADGES,
    UPDATE_VIEWED_BADGES,
    REQUEST_ACTIVITY_BADGES,
    REQUEST_EARNED_BADGES,
    REQUEST_DAILY_SUMMARY_BADGES
} from 'actions/ActionTypes';
import { LEADERBOARD_LIMIT } from '../Badges/constants/badgesPageConstants';
import {
    receiveBadgeAttributes,
    failedBadgeAttributes,
    receiveLatestEarnedBadges,
    failedLatestEarnedBadges,
    failedAvailableBadges,
    receiveAvailableBadges,
    receiveBadgesLeaderboard,
    failedBadgesLeaderboard,
    receiveComparisonBadges,
    failedComparisonBadges,
    onBadgeMarkedAsSeen,
    receiveActivityBadges,
    failedActivityBadges,
    receiveEarnedBadges,
    failedEarnedBadges,
    receiveDailySummaryBadges,
    failedDailySummaryBadges
} from '../actions/badgesActions';
import { gcApi } from '../config/axios';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';

// feature flag
import ViewerSocialProfileModel from 'models/user/ViewerSocialProfile';
import { getSystemPreferenceValue } from 'reducers/system/systemPreferences';
import {
    gcPlusExBadgesRoleKey // Clean up ticket: CA-171397
} from 'models/preferences/PreferencesMapping';

let badgeSagas = [];

export function* shouldShowExclusiveBadge() {
    const gcPlusExBadgesRoleValue = yield select(getSystemPreferenceValue, gcPlusExBadgesRoleKey); // Clean up ticket: CA-171397
    const gcPlusExBadgeFeatureOn = ViewerSocialProfileModel.hasRole(gcPlusExBadgesRoleValue);

    if (!gcPlusExBadgeFeatureOn) {
        return undefined;
    }

    return true;
}

export function* fetchBadgeAttributes() {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/attributes`);

        yield put(receiveBadgeAttributes(response.data));
    } catch (e) {
        yield put(failedBadgeAttributes());
    }
}
badgeSagas.push(takeEvery(REQUEST_BADGE_ATTRIBUTES, fetchBadgeAttributes));

export function* fetchLatestEarnedBadges({ displayName }) {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/earned/latest/${displayName}`);

        yield put(receiveLatestEarnedBadges(response.data));
    } catch (e) {
        yield put(failedLatestEarnedBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_LATEST_EARNED_BADGES, fetchLatestEarnedBadges));

export function* fetchBadgesLeaderboard({ start, limit }) {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/leaderboard`, {
            params: {
                start,
                limit: limit || LEADERBOARD_LIMIT
            }
        });

        yield put(receiveBadgesLeaderboard(response.data, !!start));
        yield fork(fetchLeaderboardProfileImageAndFrameSaga, response.data);
    } catch (e) {
        yield put(failedBadgesLeaderboard());
    }
}
badgeSagas.push(takeEvery(FETCH_BADGES_LEADERBOARD, fetchBadgesLeaderboard));

export function* fetchAvailableBadges({ categoryId, difficultyId, start, limit, showExclusive }) {
    let showExclusiveBadge = showExclusive || undefined;
    if (showExclusiveBadge) {
        showExclusiveBadge = yield call(shouldShowExclusiveBadge);
    }

    try {
        const response = yield call(gcApi.get, `/badge-service/badge/available`, {
            params: {
                categoryId,
                difficultyId,
                start,
                limit,
                showExclusiveBadge
            }
        });

        yield put(receiveAvailableBadges(response.data));
    } catch (e) {
        yield put(failedAvailableBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_AVAILABLE_BADGES, fetchAvailableBadges));

export function* fetchComparisonBadges({ displayName, categoryId, difficultyId, start, limit }) {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/compare/${displayName}`, {
            params: {
                categoryId,
                difficultyId,
                start,
                limit
            }
        });

        yield put(receiveComparisonBadges(response.data));
    } catch (e) {
        yield put(failedComparisonBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_COMPARISON_BADGES, fetchComparisonBadges));

export function* markBadgeAsSeen({ badgeIds }) {
    try {
        const response = yield call(gcApi.put, '/badge-service/badge/badgesviewed', badgeIds);

        yield put(onBadgeMarkedAsSeen(response.data));
    } catch (e) {}
}
badgeSagas.push(takeEvery(UPDATE_VIEWED_BADGES, markBadgeAsSeen));

export function* fetchActivityBadges({ displayName, activityId }) {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/${displayName}/earned/activity/${activityId}`);

        yield put(receiveActivityBadges(response.data));
    } catch (e) {
        yield put(failedActivityBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_ACTIVITY_BADGES, fetchActivityBadges));

export function* fetchEarnedBadges({ categoryId, difficultyId, start, limit }) {
    try {
        const response = yield call(gcApi.get, `/badge-service/badge/earned`, {
            params: {
                categoryId,
                difficultyId,
                start,
                limit
            }
        });

        yield put(receiveEarnedBadges(response.data));
    } catch (e) {
        yield put(failedEarnedBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_EARNED_BADGES, fetchEarnedBadges));

export function* fetchDailySummaryBadges({ displayName, calendarDate }) {
    try {
        const response = yield call(
            gcApi.get,
            `/badge-service/badge/${displayName}/earned/dailysummary/${calendarDate}`
        );

        yield put(receiveDailySummaryBadges(response.data));
    } catch (e) {
        yield put(failedDailySummaryBadges());
    }
}
badgeSagas.push(takeEvery(REQUEST_DAILY_SUMMARY_BADGES, fetchDailySummaryBadges));

/// section: fetching profile image and frame

/**
 * handle fetch likers response. (use in opening liker dialog)
 * @param {Object} responseData
 * @param {Object[]} responseData.connections
 * @param {number} responseData.connections[].userProfileId
 */
function* fetchLeaderboardProfileImageAndFrameSaga(responseData) {
    if (!responseData?.connections?.length) {
        return;
    }
    const userIds = responseData.connections.map((item) => item.userProfileId);

    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

// watcher
export { badgeSagas };
